import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Crown, Calendar } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";

const includes = [
  "One free 1-on-1 session with Angela",
  "Discounted ongoing sessions at $65 (regularly $125)",
  "Two live monthly teachings",
  "Private community access",
  "Guest experts and industry leaders",
  "12 weeks of pre-recorded academy modules",
  "6 months access to course materials",
];

const PricingSection = () => {
  const { openCheckout, checkoutElement } = useStripeCheckout();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [scholarshipCode, setScholarshipCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [showScholarship, setShowScholarship] = useState(false);

  const validateContact = () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Please enter your first and last name.");
      return false;
    }
    if (!phone.trim()) {
      toast.error("Please enter a phone number so Angela can reach you.");
      return false;
    }
    if (!email.trim()) {
      toast.error("Please enter your email.");
      return false;
    }
    return true;
  };

  const saveLead = async (source: string) => {
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    await supabase.from("leads").insert({
      name: fullName,
      email: email.trim().toLowerCase(),
      phone: phone.trim(),
      source,
    });
  };

  const handleEnroll = async (priceId: string) => {
    if (!validateContact()) return;
    await saveLead(`checkout_${priceId}`);
    openCheckout({ priceId, customerEmail: email });
  };

  const handleRedeem = async () => {
    if (!validateContact()) return;
    if (!scholarshipCode) return toast.error("Enter a scholarship code.");
    setRedeeming(true);
    await saveLead("scholarship_redeem");
    const { data, error } = await supabase.functions.invoke("redeem-scholarship", {
      body: { code: scholarshipCode, email },
    });
    setRedeeming(false);
    if (error || data?.error) {
      toast.error(data?.error || error?.message || "Invalid code");
      return;
    }
    toast.success("Scholarship applied! Check your email for a sign-in link.");
    setScholarshipCode("");
  };

  return (
    <section id="pricing" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_hsl(43_72%_55%_/_0.05),_transparent_70%)]" />
      <div className="container mx-auto px-6 md:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14 space-y-4"
        >
          <p className="text-xs uppercase tracking-[0.4em] text-gold font-body font-medium">Your Investment</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground">
            Choose Your <span className="text-gradient-gold">Next Step</span>
          </h2>
          <p className="text-muted-foreground font-body max-w-2xl mx-auto">
            Full academy enrollment or a private 1:1 session with Angela.
          </p>
        </motion.div>

        {/* Contact capture */}
        <div className="max-w-md mx-auto mb-10 space-y-3">
          <label className="block text-xs uppercase tracking-[0.25em] text-muted-foreground font-body text-center">
            Your details
          </label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              maxLength={100}
              className="w-full px-4 py-3 bg-secondary/50 border border-gold/20 text-foreground font-body text-sm focus:outline-none focus:border-gold/60"
            />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
              maxLength={100}
              className="w-full px-4 py-3 bg-secondary/50 border border-gold/20 text-foreground font-body text-sm focus:outline-none focus:border-gold/60"
            />
          </div>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone number"
            maxLength={30}
            className="w-full px-4 py-3 bg-secondary/50 border border-gold/20 text-foreground font-body text-sm focus:outline-none focus:border-gold/60"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-4 py-3 bg-secondary/50 border border-gold/20 text-foreground font-body text-sm focus:outline-none focus:border-gold/60"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* Academy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="border border-gold/30 bg-card/60 backdrop-blur-md p-8 md:p-10 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-gold" />
              <span className="text-xs uppercase tracking-[0.3em] text-gold font-body">The Academy</span>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-5xl md:text-6xl font-display font-bold text-gradient-gold">$500</span>
              <span className="text-muted-foreground font-body text-sm">/ one-time</span>
            </div>
            <p className="text-muted-foreground font-body text-sm mb-6">12-week virtual, pre-recorded program</p>

            <ul className="space-y-2.5 mb-6">
              {includes.map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-gold shrink-0 mt-1" />
                  <span className="text-foreground/90 font-body text-sm">{item}</span>
                </li>
              ))}
            </ul>

            {!showScholarship && (
              <button
                onClick={() => handleEnroll("wta_academy_enrollment")}
                className="w-full py-4 bg-gold text-background hover:bg-gold-light font-body uppercase tracking-[0.25em] text-xs font-semibold transition-colors"
              >
                Enroll in Academy
              </button>
            )}

            <div className={showScholarship ? "" : "mt-4 text-center"}>
              <button
                onClick={() => {
                  setShowScholarship(!showScholarship);
                  if (showScholarship) setScholarshipCode("");
                }}
                className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-gold font-body"
              >
                {showScholarship ? "← Pay with card instead" : "Have a scholarship code?"}
              </button>
            </div>
            {showScholarship && (
              <div className="mt-4 space-y-3">
                <input
                  type="text"
                  value={scholarshipCode}
                  onChange={(e) => setScholarshipCode(e.target.value.toUpperCase())}
                  placeholder="SCHOLARSHIP CODE"
                  className="w-full px-4 py-3 bg-secondary/50 border border-gold/20 text-foreground font-body text-sm uppercase tracking-widest text-center focus:outline-none focus:border-gold/60"
                />
                <button
                  onClick={handleRedeem}
                  disabled={redeeming}
                  className="w-full py-4 bg-gold text-background hover:bg-gold-light font-body uppercase tracking-[0.25em] text-xs font-semibold transition-colors disabled:opacity-50"
                >
                  {redeeming ? "Redeeming..." : "Redeem & Enroll Free"}
                </button>
                <p className="text-xs text-muted-foreground font-body text-center">
                  No payment required. We'll email you a secure sign-in link.
                </p>
              </div>
            )}
          </motion.div>

          {/* 1:1 Session */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="border border-gold/20 bg-card/40 backdrop-blur-md p-8 md:p-10 relative overflow-hidden flex flex-col"
          >
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-gold" />
              <span className="text-xs uppercase tracking-[0.3em] text-gold font-body">1:1 Session</span>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-5xl md:text-6xl font-display font-bold text-gradient-gold">$125</span>
              <span className="text-muted-foreground font-body text-sm">/ 60 min</span>
            </div>
            <p className="text-muted-foreground font-body text-sm mb-6">
              Private coaching session directly with Angela.
            </p>

            <ul className="space-y-2.5 mb-6 flex-1">
              <li className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-gold shrink-0 mt-1" />
                <span className="text-foreground/90 font-body text-sm">60 minutes 1:1 with Angela</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-gold shrink-0 mt-1" />
                <span className="text-foreground/90 font-body text-sm">Book instantly after checkout</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-gold shrink-0 mt-1" />
                <span className="text-foreground/90 font-body text-sm">Personalized guidance on your business, wealth, or calling</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-gold shrink-0 mt-1" />
                <span className="text-foreground/90 font-body text-sm text-muted-foreground">
                  Academy enrollees pay only $65 (unlocked in members area)
                </span>
              </li>
            </ul>

            <button
              onClick={() => handleEnroll("wta_session_60")}
              className="w-full py-4 border border-gold text-gold hover:bg-gold hover:text-background font-body uppercase tracking-[0.25em] text-xs font-semibold transition-colors"
            >
              Book a Session
            </button>
          </motion.div>
        </div>

        <p className="text-center text-xs text-muted-foreground font-body mt-8">
          Klarna, Afterpay, Cash App Pay, and all major cards accepted at checkout.
        </p>
      </div>
      {checkoutElement}
    </section>
  );
};

export default PricingSection;
